// @ts-nocheck

import React, { useCallback } from 'react';
import styled from 'styled-components';

import { Target } from '../../client';
import MissionBannerContainer from '../../modules/missions/components/MissionBanner/MissionBannerContainer';
import MissionCardContainer from '../../modules/missions/components/MissionCard/MissionCardContainer';
import { type MatchState, MatchEntity } from '../../states/entities/matches';
import { CardModes, type CardMode } from '../../types/common';
import { type SeparatorType } from '../../types/separator';
import { SportIds } from '../../types/sports';
import { match as MatchUtils } from '../../utils';
import BoostedOddCardContainer from '../BoostedOddCard/BoostedOddCardContainer';
import MatchCard from '../MatchCard';
import MatchCardOutrightContainer from '../MatchCardOutright/MatchCardOutrightContainer';
import MatchCardSeparator from '../MatchCardSeparator';
import MatchPromoCard from '../MatchPromoCard';

const StyledMissionBannerContainer = styled(MissionBannerContainer)`
  padding: ${Target.__MOBILE__ ? '0.2em 0.6em 0.9em' : '1em 0.9em 1em'};
`;

const StyledMissionCardContainer = styled(MissionCardContainer)`
  padding: ${Target.__MOBILE__ ? '0.2em 0.6em 0.9em' : '1em 0.9em 1em'};
`;

export type RowFactoryProps = {
  betsData: Array<{ betId: number; isMultichoice: boolean }>;
  categoryId: number;
  categoryName: string;
  competitor1Name: string;
  competitor1Flag?: string;
  competitor1NbRedCards?: number;
  competitor2Name: string;
  competitor2Flag?: string;
  competitor2NbRedCards?: number;
  image?: string;
  isFavorite: boolean;
  cardMode?: CardMode;
  isEventLive?: boolean;
  isOutright?: boolean;
  mainBetId: number;
  match: MatchState;
  matchId: number;
  matchStart: number;
  moreBets: number;
  onFavoriteButtonClicked: (isFavorite: boolean) => void;
  periodName: string;
  roundName: string;
  topName?: string;
  score?: string;
  separatorType: SeparatorType;
  setScores: string;
  showFavorite: boolean;
  sportId: number;
  text?: string | null;
  title: string;
  tournamentName: string;
  flag: string;
  tvChannels: string;
  promotionImage?: string;
  promotionUrl?: string;
  promotionWamUrl?: string;
  promotionMissionId?: number;
  to: string;
  openExternalLink: (url: string, options?: ExternalLinkOptions) => void;
  areMatchLineUpsAvailable?: boolean;
  betCategoryId?: number;
  expandId: string;
  largeMissionCard?: boolean;
};

// C'est ici qu'on changera en fonction de si on veut afficher une promo/cote boostée/match card/separateur outright.
const RowFactory = ({
  separatorType,
  matchId,
  text,
  cardMode,
  isEventLive,
  isOutright,
  mainBetId,
  moreBets,
  sportId,
  matchStart,
  title,
  image,
  competitor1Name,
  competitor1Flag,
  competitor1NbRedCards,
  competitor2Name,
  competitor2Flag,
  competitor2NbRedCards,
  flag,
  categoryName,
  tournamentName,
  topName,
  roundName,
  score,
  setScores,
  tvChannels,
  betsData,
  showFavorite,
  isFavorite,
  onFavoriteButtonClicked: onFavClick,
  promotionImage,
  promotionUrl,
  promotionWamUrl,
  promotionMissionId,
  periodName,
  to,
  openExternalLink,
  categoryId,
  areMatchLineUpsAvailable = false,
  betCategoryId,
  expandId,
  largeMissionCard = false,
}: RowFactoryProps) => {
  const onFavoriteButtonClicked = useCallback(() => onFavClick(isFavorite), [isFavorite, onFavClick]);

  if (separatorType) {
    return <MatchCardSeparator text={text} />;
  }
  if (cardMode === CardModes.PROMO || cardMode === CardModes.MISSIONS_BANNER) {
    if (promotionMissionId) {
      if (largeMissionCard) {
        return <StyledMissionCardContainer missionId={promotionMissionId} />;
      }
      return <StyledMissionBannerContainer missionId={promotionMissionId} />;
    }

    if (promotionImage) {
      return (
        <MatchPromoCard
          promotionImage={promotionImage}
          promotionUrl={promotionUrl}
          promotionWamUrl={promotionWamUrl}
          openExternalLink={openExternalLink}
        />
      );
    }

    return null;
  }
  if (matchId) {
    if (cardMode === CardModes.BOOSTED || cardMode === CardModes.BIG_BOOSTED || cardMode === CardModes.XMAS_BOOSTED) {
      return (
        <BoostedOddCardContainer
          matchId={matchId}
          cardMode={cardMode}
          isLive={isEventLive}
          showFavorite={showFavorite}
          isFavorite={isFavorite}
          onFavoriteButtonClicked={onFavoriteButtonClicked}
        />
      );
    }
    if (isOutright) {
      return (
        <MatchCardOutrightContainer
          matchId={matchId}
          betId={mainBetId}
          moreBets={moreBets}
          sportId={sportId}
          image={image}
          isLive={isEventLive}
          tvChannel={tvChannels}
          startDate={matchStart}
          title={title}
          showFavorite={showFavorite}
          isFavorite={isFavorite}
          onFavoriteButtonClicked={onFavoriteButtonClicked}
          to={to}
          categoryId={categoryId}
          flagId={flag}
          betCategoryId={betCategoryId}
          expandId={expandId}
        />
      );
    }

    let scores = null;
    let score1 = null;
    let score2 = null;
    let psoScore = null;

    if (setScores && setScores !== '') {
      scores = setScores.split(' - ');
    }

    if (sportId === SportIds.SOCCER) {
      if (scores) {
        score1 = MatchUtils.computeScore(scores, 0, 3);
        score2 = MatchUtils.computeScore(scores, 1, 3);
      }
      // On calcul les scores de penalty que pour le Foot
      psoScore = MatchEntity.getPsoScore(scores);
    } else {
      ({ score1, score2 } = MatchEntity.getScores({ score }));
    }

    let subTitle = topName || `${categoryName ?? ''} - ${tournamentName ?? ''}`;

    if (roundName) {
      subTitle = `${subTitle} - ${roundName}`;
    }

    const matchCardProps = {
      image,
      matchId: matchId,
      moreBets: moreBets,
      sportId: sportId,
      competitor1Name,
      competitor2Name,
      flag1: competitor1Flag,
      flag2: competitor2Flag,
      subTitle,
      flagId: flag,
      score1,
      score2,
      isLive: isEventLive,
      tvChannel: tvChannels,
      startDate: matchStart,
      betsData,
      showFavorite,
      isFavorite,
      onFavoriteButtonClicked,
      periodName,
      to,
      categoryId,
      competitor1NbRedCards,
      competitor2NbRedCards,
      psoScore,
      areMatchLineUpsAvailable,
      expandId,
    };

    return <MatchCard {...matchCardProps} />;
  }
  return null;
};

export default React.memo<RowFactoryProps>(RowFactory);
